import download from 'downloadjs';
import Fuse from 'fuse.js';
import { getOr, isEqual, isNull, uniq, unset } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { createReport, deleteReportTemplate, fetchAllDocumentReportTemplates, fetchAvailableReportFields, upsertReportTemplate } from '../../../../services/report';
import { dotsToDashes } from '../../../../utils/regex-utils';
import { MimeType } from '../../../constants/MimeType';
import { OpenFieldSection, SearchFieldSection } from '../../../datasets/instances/store/types';
import { DefinitionHierarchy, DocumentSpecificHiddenFields, HiddenFields } from '../../../datasets/store/types';
import { SearchFilter } from '../../search/store/types';
import {
    calculateSearchFieldSections,
    deleteReportTemplateFailed,
    deleteReportTemplateStarted,
    deleteReportTemplateSuccessful,
    expandAllFieldSections,
    fetchAllReportTemplatesFailed,
    fetchAllReportTemplatesStarted,
    fetchAllReportTemplatesSuccessful,
    fetchAvailableDocumentFieldsSuccessful,
    fetchAvailableFieldsFailed,
    fetchAvailableFieldsStarted,
    generateReportFailed,
    generateReportSuccessful,
    openDocumentReportTemplate,
    resetReport,
    resetSearchFieldSections,
    saveReportTemplateFailed,
    saveReportTemplateStarted,
    saveReportTemplateSuccessful,
    setAllSearchFieldSections,
    setFuzzyMatchSearchValue,
    setReportAgreementTypes,
    setReportPage,
    setReportUpdated,
    setSearchFieldSection,
    setSearchFuzzyMatches,
    setSelectedAgreementTypeId,
    setSelectedTab,
    updateDocumentNameIds,
    updateDocumentReportFilter,
    updateReportAgreementTypes
} from './actions';
import { initialDocumentReportFilter } from './reducer';
import { getAllReportFields, getAllSectionsAndFields, getClientHiddenFields, getDefinitionHierarchy, getDocumentSpecificHiddenFields, getRelevantDocumentNameIds, getReportFields, getReportFilters, getReportMetaFields, getReportOpenFieldSections, getSelectedAgreementTypeId, getSelectedTemplate, getUpdatedAgreementTypeDocumentNameIds, getUpdatedDocumentNameIds, getUpsertReportTemplate } from './selectors';
import { AgreementTypeReportFields, DocumentMetaReportField, DocumentReportField, DocumentReportTemplate, DocumentReportingActionTypes, ReportPage, ReportTab, UpsertDocumentReportTemplate } from './types';
import { calculateOpenFieldSections, getAllFlattenedSearchFields } from './utils';

export function* attemptFetchAvailableReportFields({ payload }: ReturnType<typeof fetchAvailableFieldsStarted>) {
    try {
        const { agreementTypeId } = payload;
        if (!isNull(agreementTypeId)) {
            const { definitionHierarchy, hiddenFields, documentHiddenFields }: { definitionHierarchy: DefinitionHierarchy[]; hiddenFields: HiddenFields; documentHiddenFields: DocumentSpecificHiddenFields; } = yield call(fetchAvailableReportFields, { agreementTypeId });
            yield put(fetchAvailableDocumentFieldsSuccessful(definitionHierarchy, hiddenFields, documentHiddenFields));
            const relevantDocumentNameIds: string[] = yield select(getRelevantDocumentNameIds(agreementTypeId));
            yield put(calculateSearchFieldSections(definitionHierarchy, hiddenFields, documentHiddenFields, relevantDocumentNameIds));
        } else {
            yield put(fetchAvailableDocumentFieldsSuccessful([], {}, {}));
            yield put(resetSearchFieldSections());
        }

    } catch (e) {
        yield put(fetchAvailableFieldsFailed((e as Error).message));
        toast.error('Unable to retrieve the available report fields. Please try again.');
    }
}

function* fetchAvailableReportFieldsWatcher() {
    yield takeLatest(DocumentReportingActionTypes.FETCH_AVAILABLE_FIELDS_STARTED, attemptFetchAvailableReportFields);
}

export function* fetchAgreementTypeAvailableFields({ payload }: ReturnType<typeof setSelectedAgreementTypeId>) {
    const agreementTypeId = payload;
    yield put(fetchAvailableFieldsStarted(agreementTypeId));
}

function* updateSelectedAgreementTypeWatcher() {
    yield takeLatest(DocumentReportingActionTypes.UPDATE_REPORT_SELECTED_AGREEMENT_TYPE, fetchAgreementTypeAvailableFields);
}

export function* triggerFetchAvailableReports({ payload }: ReturnType<typeof updateReportAgreementTypes>) {
    const value = payload;
    const selectedAgreementTypeId: number | null = yield select(getSelectedAgreementTypeId);
    const updatedDocumentNameIds: string[] = yield select(getUpdatedDocumentNameIds(value));
    yield put(updateDocumentReportFilter('documentNameId', updatedDocumentNameIds));
    yield put(setReportAgreementTypes(value));
    if (isNull(value)) {
        yield put(setSelectedAgreementTypeId(null));
        yield put(resetSearchFieldSections());
    } else {
        if ((!isNull(selectedAgreementTypeId) && !value.includes(selectedAgreementTypeId.toString())) || isNull(selectedAgreementTypeId)) {
            yield put(setSelectedAgreementTypeId(parseInt(value[0])));
        }
    }
}

function* updateAgreementTypesWatcher() {
    yield takeLatest(DocumentReportingActionTypes.UPDATE_REPORT_AGREEMENT_TYPES, triggerFetchAvailableReports);
}

export function* attemptCheckSearchFieldSections({ payload }: ReturnType<typeof updateDocumentNameIds>) {
    const { documentNameIds, agreementTypeId } = payload;
    const selectedAgreementTypeId: number | null = yield select(getSelectedAgreementTypeId);
    yield put(updateDocumentReportFilter('documentNameId', documentNameIds));
    if (!isNull(selectedAgreementTypeId) && agreementTypeId === selectedAgreementTypeId) {
        const updatedAgreementTypeDocumentNameIds: string[] = yield select(getUpdatedAgreementTypeDocumentNameIds(documentNameIds, agreementTypeId));
        const definitionHierarchy: DefinitionHierarchy[] = yield select(getDefinitionHierarchy);
        const hiddenFields: HiddenFields = yield select(getClientHiddenFields);
        const documentHiddenFields: DocumentSpecificHiddenFields = yield select(getDocumentSpecificHiddenFields);
        yield put(calculateSearchFieldSections(definitionHierarchy, hiddenFields, documentHiddenFields, updatedAgreementTypeDocumentNameIds));
    }
}

function* updateDocumentNameIdsWatcher() {
    yield takeEvery(DocumentReportingActionTypes.UPDATE_REPORT_DOCUMENT_NAME_IDS, attemptCheckSearchFieldSections);
}

export function* attemptGenerateReport() {
    try {
        const reportFields: AgreementTypeReportFields = yield select(getAllReportFields);
        const metaFields: DocumentMetaReportField[] = yield select(getReportMetaFields);
        const filters: SearchFilter = yield select(getReportFilters);
        const selectedTemplate: DocumentReportTemplate | null = yield select(getSelectedTemplate);
        const fileName = selectedTemplate ? selectedTemplate.name : 'Ark51 Custom Report';
        const formattedFileName = dotsToDashes(fileName);
        const doubleRowHeight = navigator.userAgent.indexOf('Win') !== -1;
        const report: Blob = yield call(createReport, { reportFields, filters, fileName, doubleRowHeight, metaFields });
        yield call(download, report, formattedFileName, MimeType.EXCEL);
        yield put(generateReportSuccessful());
    } catch (e) {
        yield put(generateReportFailed((e as Error).message));
        toast.error('Unable to generate report. Please try again.');
    }
}

function* generateReportWatcher() {
    yield takeLeading(DocumentReportingActionTypes.GENERATE_REPORT_STARTED, attemptGenerateReport);
}

export function* checkDocumentReportUpdated() {
    try {
        const reportFields: DocumentReportField[] = yield select(getReportFields);
        const filters: SearchFilter = yield select(getReportFilters);
        const selectedTemplate: DocumentReportTemplate | null = yield select(getSelectedTemplate);
        const selectedTemplateFields = getOr([], 'reportFields', selectedTemplate);
        const selectedReportFilters = getOr(initialDocumentReportFilter, 'filters', selectedTemplate);
        const reportFieldsUpdated = !isEqual({ reportFields: selectedTemplateFields, filters: selectedReportFilters }, { reportFields, filters });
        yield put(setReportUpdated(reportFieldsUpdated));
    } catch (e) {
        toast.error('Something went wrong when checking if report has been updated.');
    }
}

function* documentReportUpdatedWatcher() {
    yield takeEvery([
        DocumentReportingActionTypes.UPDATE_DOCUMENT_REPORT_FIELDS,
        DocumentReportingActionTypes.ADD_DOCUMENT_REPORT_FIELD,
        DocumentReportingActionTypes.REMOVE_DOCUMENT_REPORT_FIELD,
        DocumentReportingActionTypes.UPDATE_DOCUMENT_REPORT_FILTER,
        DocumentReportingActionTypes.SET_DOCUMENT_REPORT_META_FIELDS,
        DocumentReportingActionTypes.UPDATE_INSTANCE_REPORT_FIELD_CUSTOM_LABEL,
        DocumentReportingActionTypes.UPDATE_INSTANCE_REPORT_FIELD_INCLUDE_DATASET_TITLE
    ], checkDocumentReportUpdated);
}

export function* attemptSaveReportTemplate({ payload }: ReturnType<typeof saveReportTemplateStarted>) {
    try {
        const saveAs = payload;
        let reportTemplate: UpsertDocumentReportTemplate = yield select(getUpsertReportTemplate);
        if (saveAs) {
            reportTemplate = unset('reportTemplateId', reportTemplate);
        }
        const upsertedTemplate: DocumentReportTemplate = yield call(upsertReportTemplate, reportTemplate);
        yield put(saveReportTemplateSuccessful(upsertedTemplate));
        yield put(fetchAllReportTemplatesStarted());
        toast('Successfully saved report template');
    } catch (e) {
        yield put(saveReportTemplateFailed((e as Error).message));
        toast.error('Unable to save report template. Please try again.');
    }
}

function* saveReportTemplateWatcher() {
    yield takeLeading(DocumentReportingActionTypes.SAVE_REPORT_TEMPLATE_STARTED, attemptSaveReportTemplate);
}

export function* attemptFetchAllReportTemplates() {
    try {
        const reportTemplates: DocumentReportTemplate[] = yield call(fetchAllDocumentReportTemplates);
        yield put(fetchAllReportTemplatesSuccessful(reportTemplates));
    } catch (e) {
        yield put(fetchAllReportTemplatesFailed((e as Error).message));
        toast.error('Unable to fetch report templates. Please try again.');
    }
}

function* fetchAllReportTemplatesWatcher() {
    yield takeLeading(DocumentReportingActionTypes.FETCH_ALL_REPORT_TEMPLATES_STARTED, attemptFetchAllReportTemplates);
}

export function* attemptDeleteReportTemplate({ payload }: ReturnType<typeof deleteReportTemplateStarted>) {
    try {
        const reportTemplates: DocumentReportTemplate[] = yield call(deleteReportTemplate, { id: payload, isOpinionTemplate: false });
        yield put(deleteReportTemplateSuccessful(reportTemplates));
    } catch (e) {
        yield put(deleteReportTemplateFailed((e as Error).message));
        toast.error('Unable to delete report template. Please try again.');
    }
}

function* deleteReportTemplateWatcher() {
    yield takeLeading(DocumentReportingActionTypes.DELETE_REPORT_TEMPLATE_STARTED, attemptDeleteReportTemplate);
}

export function* attemptOpenTemplate(templateId: string) {
    const reportTemplates: DocumentReportTemplate[] = yield call(fetchAllDocumentReportTemplates);
    const template = reportTemplates.find(({ reportTemplateId }) => reportTemplateId.toString() === templateId);
    if (template) {
        const { reportFields } = template;
        const firstAgreementTypeId = parseInt(Object.keys(reportFields)[0]);
        yield put(openDocumentReportTemplate(template));
        yield put(setSelectedAgreementTypeId(firstAgreementTypeId));
    } else {
        yield put(push('/documents/reporting/templates'));
    }
}

export function* redirectReportPage({ payload }: ReturnType<typeof setReportPage>) {
    const { page, reportTemplateId } = payload;
    if (reportTemplateId) {
        yield call(attemptOpenTemplate, reportTemplateId);
    } else {
        let navigationPath = '/documents/reporting';
        if (page !== ReportPage.SELECT) {
            navigationPath = `/documents/reporting/${page === ReportPage.WIZARD ? 'create' : 'templates'}`;
        } else {
            yield put(resetReport(ReportPage.SELECT));
        }
        yield put(push(navigationPath));
    }
}

function* redirectReportPageWatcher() {
    yield takeLatest(DocumentReportingActionTypes.SELECT_REPORT_PAGE, redirectReportPage);
}

export function* reselectFirstAgreementType({ payload }: ReturnType<typeof setSelectedTab>) {
    if ([ReportTab.FIELDS, ReportTab.CONFIGURE].includes(payload)) {
        const allReportFields: AgreementTypeReportFields = yield select(getAllReportFields);
        const selectedAgreementTypeId: number | null = yield select(getSelectedAgreementTypeId);
        const reportAgreementTypeIds = Object.keys(allReportFields);
        if (reportAgreementTypeIds.length > 1) {
            const firstAgreementTypeId = parseInt(reportAgreementTypeIds[0]);
            if (selectedAgreementTypeId !== firstAgreementTypeId) {
                yield put(setSelectedAgreementTypeId(parseInt(reportAgreementTypeIds[0])));
            }
        }
    }
}

function* resetSelectedAgreementTypeIdWatcher() {
    yield takeLatest(DocumentReportingActionTypes.SELECT_WIZARD_TAB, reselectFirstAgreementType);
}

export function* redirectDocumentTemplatePage({ payload }: ReturnType<typeof openDocumentReportTemplate>) {
    const navigationPath = `/documents/reporting/templates/${payload.reportTemplateId}`;
    yield put(push(navigationPath));
}

function* redirectDocumentTemplatePageWatcher() {
    yield takeLeading(DocumentReportingActionTypes.OPEN_DOCUMENT_REPORT_TEMPLATE, redirectDocumentTemplatePage);
}

export function* attemptOpenRelevantSearchFieldSections({ payload }: ReturnType<typeof setSearchFieldSection>) {
    if (!isNull(payload)) {
        const definitionHierarchy: DefinitionHierarchy[] = yield select(getDefinitionHierarchy);
        const currentOpenFieldsAndSections: OpenFieldSection[] = yield select(getReportOpenFieldSections);
        const openFieldsAndSections = calculateOpenFieldSections(payload, definitionHierarchy);
        if (currentOpenFieldsAndSections !== openFieldsAndSections) {
            yield put(expandAllFieldSections(uniq([...currentOpenFieldsAndSections, ...openFieldsAndSections])));
        }
    }
}

export function* attemptFindFuzzySearchResults({ payload }: ReturnType<typeof setFuzzyMatchSearchValue>) {
    const searchFields: SearchFieldSection[] = yield select(getAllSectionsAndFields);
    const searchFieldsFuse = new Fuse(searchFields, { keys: ['label', 'sectionLabel'], threshold: 0.2 });
    // Order fuzzy matches by their relevance and displaying matching sections first
    const fuzzyMatches = searchFieldsFuse.search(payload).map(({ item }) => item).sort((a, b) => b.type.localeCompare(a.type));
    yield put(setSearchFuzzyMatches(fuzzyMatches));
}

export function* attemptCalculateSearchFieldSections({ payload }: ReturnType<typeof calculateSearchFieldSections>) {
    const { definitionHierarchy, clientHiddenFields, documentHiddenFields, documentNameIds } = payload;
    const definitions = definitionHierarchy.map(({ definition }) => definition);
    const datasetIds = definitions.map(({ datasetId }) => datasetId!);
    const hiddenFields = datasetIds.reduce((acc, datasetId) => {
        const documentHiddenFieldsForDatasetId = documentHiddenFields[datasetId];
        if (documentHiddenFieldsForDatasetId.length > 0) {
            const filteredHiddenFields = documentHiddenFieldsForDatasetId.filter(hiddenDocumentField => hiddenDocumentField.documentNameIds.some(documentNameId => documentNameIds.includes(documentNameId.toString())));
            filteredHiddenFields.length > 0 && filteredHiddenFields.map(field => acc[datasetId].push(unset('documentNameIds', field)));
        }
        return acc;
    }, clientHiddenFields);
    const searchFields = getAllFlattenedSearchFields(definitionHierarchy, hiddenFields);
    yield put(setAllSearchFieldSections(searchFields));
}

function* openRelevantSearchFieldsAndSectionsWatcher() {
    yield takeLeading(DocumentReportingActionTypes.SET_REPORT_SEARCH_FIELD_SECTION, attemptOpenRelevantSearchFieldSections);
}

function* updateFuzzySearchValueWatcher() {
    yield debounce(1000, DocumentReportingActionTypes.SET_REPORT_DATASETS_FUZZY_MATCH_SEARCH_VALUE, attemptFindFuzzySearchResults);
}

function* calculateSearchFieldSectionsWatcher() {
    yield takeLeading(DocumentReportingActionTypes.CALCULATE_ALL_REPORT_SEARCH_FIELDS, attemptCalculateSearchFieldSections);
}

export function* documentReportingSaga() {
    yield all([
        fork(updateAgreementTypesWatcher),
        fork(updateDocumentNameIdsWatcher),
        fork(updateSelectedAgreementTypeWatcher),
        fork(fetchAvailableReportFieldsWatcher),
        fork(generateReportWatcher),
        fork(documentReportUpdatedWatcher),
        fork(saveReportTemplateWatcher),
        fork(fetchAllReportTemplatesWatcher),
        fork(deleteReportTemplateWatcher),
        fork(redirectReportPageWatcher),
        fork(redirectDocumentTemplatePageWatcher),
        fork(openRelevantSearchFieldsAndSectionsWatcher),
        fork(updateFuzzySearchValueWatcher),
        fork(calculateSearchFieldSectionsWatcher),
        fork(resetSelectedAgreementTypeIdWatcher)
    ]);
}
